import { useState, type FormEvent } from "react";
import { Building2, Check, Plus, ShieldCheck } from "lucide-react";

import {
  activateIndustryTemplate,
  configureOperatingUnitType,
  industryTemplateWorkspace,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading } from "@/components/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import { Textarea } from "@/components/ui/textarea";
import { titleCase } from "@/lib/utils";

export function IndustryTemplates({
  organisationId,
  refresh,
  canManage,
  changed,
}: {
  organisationId: string;
  refresh: number;
  canManage: boolean;
  changed: () => void;
}) {
  const { data, error, loading } = useData(
    () => industryTemplateWorkspace(organisationId),
    [organisationId, refresh],
  );
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");
  const [unitOpen, setUnitOpen] = useState(false);

  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  const active = data.activations.find((row) => row.status === "active");

  async function activate(templateId: string) {
    setBusy(templateId);
    setActionError("");
    try {
      await activateIndustryTemplate(organisationId, templateId);
      changed();
    } catch (reason) {
      setActionError((reason as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="OPTIONAL INDUSTRY CONFIGURATION"
        title="Industry templates"
        description="Apply shared terminology and suggested controls without creating a separate product, workflow or security model."
      >
        {canManage && (
          <Button className="min-h-11" variant="outline" onClick={() => setUnitOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Operating-unit term
          </Button>
        )}
      </PageHeader>

      <section className="rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] p-4 text-sm text-[var(--warning)]">
        Activating a template never enables a block, warning, escalation or
        retrospective review. An authorised administrator must review and
        separately activate every purchase policy.
      </section>
      {actionError && <ErrorState message={actionError} />}

      <section className="grid gap-4 lg:grid-cols-2" aria-label="Tier-1 industry templates">
        {data.templates.map((template) => {
          const isActive = active?.template_version_id === template.id;
          return (
            <article className="surface p-5" key={template.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="eyebrow">{String(template.industry).toUpperCase()} · VERSION {template.version}</p>
                  <h2 className="mt-1 text-xl font-semibold">{template.name}</h2>
                </div>
                <Badge value={isActive ? "active" : template.status} />
              </div>
              <p className="mt-3 text-sm text-[var(--text-secondary)]">{template.disclaimer}</p>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                <TemplateDatum label="Operating term" value={template.terminology?.operating_unit || "Operating unit"} />
                <TemplateDatum label="Suggested departments" value={template.department_presets?.length || 0} />
                <TemplateDatum label="Suggested categories" value={template.purchase_category_presets?.length || 0} />
                <TemplateDatum label="Suggested rules" value={template.suggested_policy_rules?.length || 0} />
              </dl>
              <details className="mt-4 text-sm">
                <summary className="cursor-pointer font-semibold">Review suggested configuration</summary>
                <div className="mt-3 grid gap-3 text-[var(--text-secondary)]">
                  <SuggestionList title="Operating units" rows={template.department_presets || []} />
                  <SuggestionList title="Purchase categories" rows={template.purchase_category_presets || []} />
                  <SuggestionList title="Exception types" rows={template.suggested_exception_types || []} />
                </div>
              </details>
              {canManage && (
                <Button
                  className="mt-5 min-h-11 w-full"
                  variant={isActive ? "outline" : "default"}
                  disabled={isActive || busy === template.id}
                  onClick={() => void activate(template.id)}
                >
                  {isActive ? <Check className="size-4" aria-hidden="true" /> : <ShieldCheck className="size-4" aria-hidden="true" />}
                  {isActive ? "Template active" : busy === template.id ? "Activating…" : "Activate suggestions only"}
                </Button>
              )}
            </article>
          );
        })}
      </section>

      <section className="surface p-5" aria-labelledby="unit-types-heading">
        <div className="flex items-start gap-3">
          <Building2 className="mt-1 size-5 text-[var(--action)]" aria-hidden="true" />
          <div>
            <p className="eyebrow">SHARED OPERATING-UNIT MODEL</p>
            <h2 id="unit-types-heading" className="mt-1 text-xl font-semibold">Organisation terminology</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Property, project, site, facility, clinic and laboratory remain display terms over the shared business-unit and tenant architecture.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.terminology.map((term) => (
            <article className="rounded-xl border border-[var(--border)] p-4" key={term.id}>
              <p className="text-xs text-[var(--text-muted)]">{titleCase(term.term_key)}</p>
              <strong className="mt-1 block">{term.display_singular}</strong>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">Version {term.version}</p>
            </article>
          ))}
          {data.unitTypes.map((unit) => (
            <article className="rounded-xl border border-[var(--border)] p-4" key={unit.id}>
              <p className="text-xs text-[var(--text-muted)]">Configurable operating unit</p>
              <strong className="mt-1 block">{unit.display_singular}</strong>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">{unit.description}</p>
            </article>
          ))}
          {!data.terminology.length && !data.unitTypes.length && (
            <Empty title="No industry terminology selected" description="Existing organisations continue with their current terminology until an administrator opts in." />
          )}
        </div>
      </section>

      <OperatingUnitTypeDialog
        open={unitOpen}
        onOpenChange={setUnitOpen}
        organisationId={organisationId}
        onDone={changed}
      />
    </>
  );
}

function TemplateDatum({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-[var(--surface-secondary)] p-3">
      <dt className="text-xs text-[var(--text-muted)]">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function SuggestionList({ title, rows }: { title: string; rows: unknown[] }) {
  return (
    <div>
      <strong>{title}</strong>
      <ul className="mt-1 grid gap-1">
        {rows.map((row, index) => (
          <li key={index}>• {typeof row === "string" ? row : JSON.stringify(row)}</li>
        ))}
      </ul>
    </div>
  );
}

function OperatingUnitTypeDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await configureOperatingUnitType(organisationId, {
        typeKey: String(form.get("type_key")),
        singular: String(form.get("singular")),
        plural: String(form.get("plural")),
        description: String(form.get("description")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="OPERATING-UNIT TERMINOLOGY" title="Add an organisation term" description="This changes display and configuration only. It does not create a new tenant or security model." />
        <form className="grid gap-4 p-6 sm:grid-cols-2" onSubmit={submit}>
          {error && <div className="sm:col-span-2"><ErrorState message={error} /></div>}
          <Label>Stable key<Input name="type_key" required pattern="[a-z][a-z0-9_]{1,39}" placeholder="property" /></Label>
          <Label>Singular<Input name="singular" required placeholder="Property" /></Label>
          <Label>Plural<Input name="plural" required placeholder="Properties" /></Label>
          <Label className="sm:col-span-2">Description<Textarea name="description" required minLength={10} /></Label>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={busy} type="submit">{busy ? "Saving…" : "Save organisation term"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

